// Application Settings Modal
// ---------------------------
// There are 2 modes:
//  1. Add Application in which case all settings can be changed and createWebApp is called
//  2. Settings in which case only select settings can be changed and updateWebApp is called
//

// React imports
import React, { useState, useCallback, useEffect } from "react";

import {
  APP_SETTINGS_ACKNOWLEDGMENT_COMPONENT_NAME,
  APP_SETTINGS_APPSETTINGS_COMPONENT_NAME,
  APP_SETTINGS_PENTESTSCOPE_COMPONENT_NAME,
  APP_SETTINGS_PROJECTPROPERTIES_COMPONENT_NAME,
  APP_SETTINGS_TESTACCOUNTS_COMPONENT_NAME,
  APP_SETTINGS_VERIFYAPP_COMPONENT_NAME,
  BUTTON_NEXT,
  EMPTY_STRING,
  HEADING_STEP_CURRENT,
  HEADING_STEP_PREVIOUS,
  MANAGE_APPS_PAGE,
  SINGLE_WEBAPP_PAGE,
} from "@/constants/definitions";

import Acknowledgment from "@/components/applicationSettings/Acknowledgment";
import ApplicationSettings from "@/components/applicationSettings/ApplicationSettings";
import PentestScope from "@/components/applicationSettings/PentestScope";
import ProjectProperties from "@/components/applicationSettings/ProjectProperties";
import TestAccounts from "@/components/applicationSettings/TestAccounts";
import VerifyApplication from "@/components/applicationSettings/VerifyApplication";
import CognitoLoginClass from "@/models/cognitoLogin";
import TestUserClass from "@/models/testUser";
import UserClass from "@/models/user";
import WebApplicationClass from "@/models/webApplication";
import { useTenantEntitlements } from "@/utils/useTenantEntitlements";
import { formatShortDate } from "@/utils/date";
import { generatePath } from "react-router-dom";

interface NewApplicationProps {
  subscriptionClient: any;
  user: UserClass;
  cognitologin: CognitoLoginClass;
  listOfWebapps: WebApplicationClass[];
  updateListOfApps: (apps: WebApplicationClass[]) => void;
  navigate: (path: string) => void;
  updateCurrentWebAppCallback: (webappid: string, cb?: () => void) => void;
}

const NewApplication: React.FC<NewApplicationProps> = ({
  subscriptionClient,
  user,
  listOfWebapps,
  updateListOfApps,
  navigate,
  updateCurrentWebAppCallback,
}) => {
  // State
  const [currentView, setCurrentView] = useState<string>(APP_SETTINGS_ACKNOWLEDGMENT_COMPONENT_NAME);
  const [acknowledgmentstep, setAcknowledgmentstep] = useState<string | null>(HEADING_STEP_CURRENT);
  const [projectpropertiesstep, setProjectpropertiesstep] = useState<string | null>(null);
  const [pentestscopestep, setPentestscopestep] = useState<string | null>(null);
  const [appsettingsstep, setAppsettingsstep] = useState<string | null>(null);
  const [testaccountsstep, setTestaccountsstep] = useState<string | null>(null);
  const [verifyappstep, setVerifyappstep] = useState<string | null>(null);
  const [newWebApp, setNewWebApp] = useState<WebApplicationClass>(new WebApplicationClass());
  const [testUser1, setTestUser1] = useState<any>(null);
  const [testUser2, setTestUser2] = useState<any>(null);
  const [securityHeaderValue, setSecurityHeaderValue] = useState<string>(EMPTY_STRING);
  const [returningToProjectProperties, setReturningToProjectProperties] = useState<boolean>(false);
  const [creatingWebapp, setCreatingWebapp] = useState<boolean>(false);
  // Set when the bundled createWebApplication mutation throws — the
  // step-4 component renders this above its button row so the user
  // can see the BE rejection reason instead of being silently
  // bounced back to the same screen.
  const [createError, setCreateError] = useState<string | null>(null);

  // App-license association (Model A: an app binds to a purchased license at
  // creation). unbound = bought but not yet attached to any app.
  const {
    entitlements: tenantLicenses,
    isLoading: licensesLoading,
    error: licensesError,
  } = useTenantEntitlements();
  const unboundLicenses = tenantLicenses.filter((license) => license.status === "unbound");
  const unboundKey = unboundLicenses.map((license) => license.id).join(",");
  // No free license means no app, for EVERY tenant. There is no unbilled
  // pass-through left in the backend, so a tenant with zero licenses is simply
  // the empty case of "none free" — not an exemption from the gate.
  //
  // Gated on the read having FINISHED: `entitlements` is [] while loading, and
  // deriving the blocked state from that would flash "no licenses available" on
  // every wizard open. A read that failed still blocks (the BE would reject
  // anyway); licensesError is what lets the copy say "couldn't check" instead of
  // wrongly telling the user they have none.
  const licenseBlocked = !licensesLoading && unboundLicenses.length === 0;
  const [selectedEntitlementId, setSelectedEntitlementId] = useState<string | null>(null);
  // Auto-select the sole free license; clear a selection that is no longer free.
  useEffect(() => {
    if (unboundLicenses.length === 1) {
      setSelectedEntitlementId(unboundLicenses[0].id);
    } else if (
      selectedEntitlementId &&
      !unboundLicenses.some((license) => license.id === selectedEntitlementId)
    ) {
      setSelectedEntitlementId(null);
    }
    // Keyed on the set of free license ids so a new array identity per render
    // does not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unboundKey]);
  // The license to bind: the explicit pick, else the sole free one. Null only
  // when nothing is free, or the user hasn't chosen among several — both of
  // which the submit guard rejects before the mutation is ever sent.
  const resolvedEntitlementId =
    selectedEntitlementId ?? (unboundLicenses.length === 1 ? unboundLicenses[0].id : null);

  // Component did mount
  useEffect(() => {
    // For bg transition purposes, make sure the root classname is correct
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.className = "root";
    }
  }, []);

  /* ********************************** */
  /* CALLBACKS FROM CHILDREN COMPONENTS */
  /* ********************************** */

  // Cancel
  // -------
  // Bail out of the wizard. The webapp record doesn't exist yet on
  // any of steps 1–4 (createNewWebApp fires on the Create button at
  // the bottom of step 4), so there's nothing to clean up server-
  // side — just navigate away. Wizard local state is discarded with
  // the component on unmount. The Verify step (step 5) intentionally
  // doesn't expose Cancel because by then the record IS created; the
  // user can still leave via the nav and the partially-configured
  // app sits on the Applications page where they can verify or
  // delete it later.
  const onCancelWizard = useCallback((): void => {
    navigate(MANAGE_APPS_PAGE);
  }, [navigate]);

  // Acknowledgment
  // ---------------
  // The user has confirmed they understand the production / data /
  // service-disruption risks. Flip the flag on the in-progress webapp
  // (persisted to the backend later via createNewWebApp) and advance
  // to Project Properties.
  const acknowledgmentCallback = useCallback((): void => {
    newWebApp.testAcknowledged = true;
    setNewWebApp(newWebApp);
    setCurrentView(APP_SETTINGS_PROJECTPROPERTIES_COMPONENT_NAME);
    setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
    setProjectpropertiesstep(HEADING_STEP_CURRENT);
    setPentestscopestep(EMPTY_STRING);
    setTestaccountsstep(EMPTY_STRING);
    setAppsettingsstep(EMPTY_STRING);
    setVerifyappstep(EMPTY_STRING);
  }, [newWebApp]);

  // Project Properties
  // ---------------------
  // Name + URL captured — advance to the Pentest Scope step (API domains +
  // live scope preview). API domains are collected there, not here.
  const projectPropertiesCallback = useCallback((webapp: WebApplicationClass): void => {
    setCurrentView(APP_SETTINGS_PENTESTSCOPE_COMPONENT_NAME);
    setNewWebApp(webapp);
    setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
    setProjectpropertiesstep(HEADING_STEP_PREVIOUS);
    setPentestscopestep(HEADING_STEP_CURRENT);
    setTestaccountsstep(EMPTY_STRING);
    setAppsettingsstep(EMPTY_STRING);
    setVerifyappstep(EMPTY_STRING);
  }, []);

  // Pentest Scope
  // --------------
  // Optional API domains captured (already written onto the webapp by the
  // step). Next → Test Accounts; Previous → back to Project Properties (mark
  // it as a re-entry so the duplicate-name check is skipped there).
  const pentestScopeCallback = useCallback((name: string, webapp: WebApplicationClass): void => {
    setNewWebApp(webapp);
    if (BUTTON_NEXT === name) {
      setCurrentView(APP_SETTINGS_TESTACCOUNTS_COMPONENT_NAME);
      setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
      setProjectpropertiesstep(HEADING_STEP_PREVIOUS);
      setPentestscopestep(HEADING_STEP_PREVIOUS);
      setTestaccountsstep(HEADING_STEP_CURRENT);
      setAppsettingsstep(EMPTY_STRING);
      setVerifyappstep(EMPTY_STRING);
      setReturningToProjectProperties(false);
      return;
    }
    // Previous → Project Properties
    setCurrentView(APP_SETTINGS_PROJECTPROPERTIES_COMPONENT_NAME);
    setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
    setProjectpropertiesstep(HEADING_STEP_CURRENT);
    setPentestscopestep(EMPTY_STRING);
    setTestaccountsstep(EMPTY_STRING);
    setAppsettingsstep(EMPTY_STRING);
    setVerifyappstep(EMPTY_STRING);
    setReturningToProjectProperties(true);
  }, []);

  // Application Settings
  // ---------------------
  // On Next: create the webapp (if not yet created), save settings, accounts, and security
  // header — then navigate to the Verify step which now always has a verificationString.
  // On Previous: go back to Test Accounts with no backend operations.
  //
  const appSettingsCallback = useCallback(async (name: string, webapp: WebApplicationClass, headerValue: string): Promise<void> => {
    if (BUTTON_NEXT !== name) {
      // Previous → back to Test Accounts
      setCurrentView(APP_SETTINGS_TESTACCOUNTS_COMPONENT_NAME);
      setNewWebApp(webapp);
      setSecurityHeaderValue(headerValue);
      setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
      setProjectpropertiesstep(HEADING_STEP_PREVIOUS);
      setPentestscopestep(HEADING_STEP_PREVIOUS);
      setTestaccountsstep(HEADING_STEP_CURRENT);
      setAppsettingsstep(EMPTY_STRING);
      setVerifyappstep(EMPTY_STRING);
      setReturningToProjectProperties(false);
      return;
    }

    // Security-header validation: BE silently ignores a value sent
    // without a name, which would lose the user's input without a
    // single signal that anything went wrong. Block the wizard step
    // here so the misconfiguration surfaces immediately. The
    // ApplicationSettings step is the right place to fix it, so we
    // stay on this view rather than rolling back to an earlier step.
    if (headerValue && !webapp.securityHeaderName) {
      setCreatingWebapp(false);
      setCreateError(
        "Security header name is required when a value is set. Add the header name, or clear the value before continuing.",
      );
      return;
    }

    // App-license gate (Model A: an app binds to a purchased license). Every
    // tenant needs a free license — the backend rejects the create outright
    // otherwise, and rejects it again if its own licensing check errors. This is
    // the fail-fast UX mirror of that; the BE remains authoritative.
    if (licensesLoading) {
      setCreateError("Still checking your app licenses — try again in a moment.");
      return;
    }
    if (licenseBlocked) {
      setCreateError(
        licensesError
          ? "Couldn't check your app licenses. Try again in a moment."
          : user.isAccountOwner()
            ? "No app licenses available. Add one in Billing before creating this app."
            : "No app licenses available. Ask your Account Owner to add one before creating this app.",
      );
      return;
    }
    if (!resolvedEntitlementId) {
      setCreateError("Select an app license for this app before continuing.");
      return;
    }

    // Show spinner while creating; clear any stale error from a prior attempt
    setCreatingWebapp(true);
    setCreateError(null);

    try {
      // One-shot create: webapp metadata + RPS + security header +
      // test accounts + link scope land in a single mutation. The
      // earlier post-create chain (updateAppSettings, saveTestAccounts,
      // setSecurityHeaderForWebapp) has been removed — BE now accepts
      // these inline on CreateWebApplicationInput.
      //
      // Reject-whole semantics on test-account failure: if any one
      // account fails to create server-side, the webapp row is left
      // created. The catch below logs but doesn't auto-rollback —
      // the user can either retry from the wizard (which won't
      // re-create the webapp since webapp.id is now set) or delete
      // the partial webapp from the Applications page and start
      // over. Worth surfacing a clearer recovery affordance here in
      // a follow-up.
      if (!webapp.id) {
        const newAccounts: TestUserClass[] = [];
        if (testUser1) newAccounts.push(testUser1);
        if (testUser2) newAccounts.push(testUser2);
        await webapp.createNewWebApp(
          subscriptionClient,
          newAccounts,
          headerValue || null,
          resolvedEntitlementId,
        );
      }

      if (!webapp.id) {
        console.error("Failed to create webapp — no ID returned");
        setCreatingWebapp(false);
        return;
      }

      // Navigate to Verify — webapp.verificationString is now set by the backend
      setCurrentView(APP_SETTINGS_VERIFYAPP_COMPONENT_NAME);
      setNewWebApp(webapp);
      setSecurityHeaderValue(headerValue);
      setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
      setProjectpropertiesstep(HEADING_STEP_PREVIOUS);
      setPentestscopestep(HEADING_STEP_PREVIOUS);
      setTestaccountsstep(HEADING_STEP_PREVIOUS);
      setAppsettingsstep(HEADING_STEP_PREVIOUS);
      setVerifyappstep(HEADING_STEP_CURRENT);
      setReturningToProjectProperties(false);
    } catch (createErr) {
      console.error("Failed to create application:", createErr);
      const message =
        createErr instanceof Error
          ? createErr.message
          : "Failed to create application. Please try again.";
      setCreateError(message);
    } finally {
      setCreatingWebapp(false);
    }
  }, [
    subscriptionClient,
    testUser1,
    testUser2,
    licenseBlocked,
    licensesLoading,
    licensesError,
    resolvedEntitlementId,
    user,
  ]);

  // Test Accounts Callback
  // -----------------------
  //
  const testAccountsCallback = useCallback((
    name: string,
    updatedTestUser1: any,
    updatedTestUser2: any,
    _updateBackend: boolean
  ): void => {
    // Previous → back to Pentest Scope; Next → on to App Settings.
    // Project Properties and Pentest Scope both sit before this step, so on
    // Previous they stay marked complete and only Pentest Scope becomes current.
    var nextView                        = APP_SETTINGS_PENTESTSCOPE_COMPONENT_NAME;
    var nextPentestscopestep            = HEADING_STEP_CURRENT;
    var nextTestaccountsstep            = EMPTY_STRING;
    var nextAppsettingsstep             = EMPTY_STRING;
    var nextVerifyappstep               = EMPTY_STRING;

    if (BUTTON_NEXT === name) {
      nextView                         = APP_SETTINGS_APPSETTINGS_COMPONENT_NAME;
      nextPentestscopestep             = HEADING_STEP_PREVIOUS;
      nextTestaccountsstep             = HEADING_STEP_PREVIOUS;
      nextAppsettingsstep              = HEADING_STEP_CURRENT;
    }

    // Store test account data locally — backend writes are deferred to Finish.
    setCurrentView(nextView);
    setTestUser1(updatedTestUser1);
    setTestUser2(updatedTestUser2);
    setAcknowledgmentstep(HEADING_STEP_PREVIOUS);
    setProjectpropertiesstep(HEADING_STEP_PREVIOUS);
    setPentestscopestep(nextPentestscopestep);
    setTestaccountsstep(nextTestaccountsstep);
    setAppsettingsstep(nextAppsettingsstep);
    setVerifyappstep(nextVerifyappstep);
    setReturningToProjectProperties(false);
    // Re-entering App Settings — drop any stored error from a prior
    // failed create attempt so the user starts clean.
    if (BUTTON_NEXT === name) {
      setCreateError(null);
    }
  }, []);

  // Verify Callback
  // -----------------
  // The webapp was already created in appSettingsCallback. On Finish, just
  // register the app in the local list and navigate to it.
  //
  // Verify only fires Finish now — the Previous affordance was
  // dropped along with the button on the Verify view (going back
  // to step 4 to edit values that have already been persisted to
  // the BE invited divergence). Settings on the created app are
  // still editable from the existing-app Settings modal.
  const verifyCallback = useCallback((_name: string): void => {
    if (newWebApp.id) {
      // Build a NEW array reference so React sees a state change and
      // re-renders the Applications list. Mutating listOfWebapps in
      // place and handing back the same reference makes setState bail
      // out on Object.is equality — it only appeared to work because
      // the sibling setCurrentWebAppId + navigate forced a render.
      // Spreading copies the existing WebApplicationClass instances by
      // reference (prototypes/methods intact); only the container is new.
      // Idempotent guard: if Finish somehow fires twice, don't add the
      // app a second time.
      const alreadyListed = listOfWebapps.some((app) => app.id === newWebApp.id);
      if (!alreadyListed) {
        updateListOfApps([...listOfWebapps, newWebApp]);
      }
    }
    updateCurrentWebAppCallback(newWebApp.id);
    navigate(
      generatePath(SINGLE_WEBAPP_PAGE, { webappid: newWebApp.id })
    );
  }, [newWebApp, listOfWebapps, updateListOfApps, updateCurrentWebAppCallback, navigate]);

  /* ********************************** */
  /* CONDITIONAL RENDERING              */
  /* ********************************** */

  const RenderView = (): React.ReactElement => {
    // Render the view
    if (APP_SETTINGS_ACKNOWLEDGMENT_COMPONENT_NAME === currentView) {
      return (
        <Acknowledgment
          initiallyAcknowledged={newWebApp.testAcknowledged}
          onComplete={acknowledgmentCallback}
          onCancel={onCancelWizard}
        />
      );
    } else if (APP_SETTINGS_PROJECTPROPERTIES_COMPONENT_NAME === currentView) {
      return (
        <ProjectProperties
          webapp={newWebApp}
          newApp={true}
          returningToView={returningToProjectProperties}
          subscriptionClient={subscriptionClient}
          onComplete={projectPropertiesCallback}
          onCancel={onCancelWizard}
        />
      );
    } else if (APP_SETTINGS_PENTESTSCOPE_COMPONENT_NAME === currentView) {
      return (
        <PentestScope
          webapp={newWebApp}
          onComplete={pentestScopeCallback}
          onCancel={onCancelWizard}
        />
      );
    } else if (APP_SETTINGS_APPSETTINGS_COMPONENT_NAME === currentView) {
      return (
        <ApplicationSettings
          webapp={newWebApp}
          onComplete={appSettingsCallback}
          onCancel={onCancelWizard}
          createError={createError}
        />
      );
    } else if (APP_SETTINGS_TESTACCOUNTS_COMPONENT_NAME === currentView) {
      return (
        <TestAccounts
          testUser1={testUser1}
          testUser2={testUser2}
          onComplete={testAccountsCallback}
          onCancel={onCancelWizard}
        />
      );
    } else if (APP_SETTINGS_VERIFYAPP_COMPONENT_NAME === currentView) {
      return (
        <VerifyApplication
          webapp={newWebApp}
          onComplete={verifyCallback}
        />
      );
    }

    // Catch all — fall back to the first wizard step
    return (
      <Acknowledgment
        initiallyAcknowledged={newWebApp.testAcknowledged}
        onComplete={acknowledgmentCallback}
        onCancel={onCancelWizard}
      />
    );
  };

  /* ********************************** */
  /*               RENDER               */
  /* ********************************** */

  const steps = [
    {
      label:     "Acknowledgment",
      stepState: acknowledgmentstep,
      title:     "Before You Begin",
      explainer: "Penetration testing is intrusive. Review the risks and confirm you're authorized to test this application before proceeding.",
    },
    {
      label:     "Project Properties",
      stepState: projectpropertiesstep,
      title:     "Project Properties",
      explainer: "Name your application and enter its URL.",
    },
    {
      label:     "Pentest Scope",
      stepState: pentestscopestep,
      title:     "Pentest Scope",
      explainer: "Review what testing will cover. Your application URL is always in scope; add API domains here if your app calls APIs on a different host.",
    },
    {
      label:     "Test Accounts",
      stepState: testaccountsstep,
      title:     "Test Accounts",
      explainer: "Optional credentials used to test authenticated areas of your application, only needed if you want authenticated testing. Both accounts should be at the same privilege level, which lets us detect cross-account access issues where one user can see or modify another user's data.",
    },
    {
      label:     "Application Settings",
      stepState: appsettingsstep,
      title:     "Application Settings",
      explainer: "Control which parts of your application are tested, how fast testing runs, and configure an optional header sent with every request to identify test traffic to your infrastructure.",
    },
    {
      label:     "Verify Application",
      stepState: verifyappstep,
      title:     "Verify Application",
      explainer: "Prove ownership before testing begins. Add the meta tag to your web app, and serve a nimis-verify endpoint for each API domain — we check them before the first test runs.",
    },
  ];

  const currentStep = steps.find((step) => step.stepState === HEADING_STEP_CURRENT);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start py-16 px-4">
      <div className="w-full max-w-2xl">

        {/* Page title */}
        <h1 className="text-2xl font-semibold text-gray-900">Add Application</h1>

        {/* Step indicator */}
        <div className="flex items-start mt-12 mb-10">
          {steps.map((step, index) => {
            const isCurrent  = step.stepState === HEADING_STEP_CURRENT;
            const isComplete = step.stepState === HEADING_STEP_PREVIOUS;
            const isLast     = index === steps.length - 1;
            const stepNumber = index + 1;

            return (
              <React.Fragment key={step.label}>
                {/* Circle + label stacked vertically */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <div className={[
                    "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors",
                    isCurrent  ? "bg-blue-600 text-white" :
                    isComplete ? "bg-gray-200 text-gray-500" :
                                 "border-2 border-gray-200 text-gray-300",
                  ].join(" ")}>
                    {isComplete ? (
                      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 6.5 11.5 13 5" />
                      </svg>
                    ) : stepNumber}
                  </div>
                  <span className={[
                    "text-xs font-medium text-center leading-tight max-w-[72px]",
                    isCurrent  ? "text-blue-600" :
                    isComplete ? "text-gray-400" :
                                 "text-gray-300",
                  ].join(" ")}>
                    {step.label}
                  </span>
                </div>
                {/* Connector — sits on the circle row */}
                {!isLast && (
                  <div className={[
                    "flex-1 mx-2 h-px mt-4",
                    isComplete ? "bg-gray-300" : "bg-gray-200",
                  ].join(" ")} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* App-license panel — quota is per app, so every app binds to a
            purchased license on creation. Shown for EVERY tenant: a tenant with
            no licenses at all is blocked just the same, and hiding the panel
            there would leave the user to discover it from a mutation error.
            Withheld only until the read settles, so it can't flash "none". */}
        {!licensesLoading && (
          licenseBlocked ? (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {licensesError ? (
                <>
                  <p className="font-medium">Couldn&apos;t check your app licenses.</p>
                  <p className="mt-0.5 text-amber-700">
                    Creating an app needs a license, so this step can&apos;t continue
                    until the check succeeds. Try again in a moment.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">No app licenses available.</p>
                  <p className="mt-0.5 text-amber-700">
                    {user.isAccountOwner()
                      ? "Add an app license in Billing before creating this app."
                      : "Ask your Account Owner to add an app license before creating this app."}
                  </p>
                </>
              )}
            </div>
          ) : unboundLicenses.length > 1 ? (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white px-4 py-3">
              <label
                htmlFor="app-license-select"
                className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5"
              >
                App license
              </label>
              <select
                id="app-license-select"
                value={selectedEntitlementId ?? ""}
                onChange={(event) => setSelectedEntitlementId(event.target.value || null)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select a license…</option>
                {unboundLicenses.map((license) => (
                  <option key={license.id} value={license.id}>
                    {license.planType === "one_off" ? "One-off" : "Subscription"} license
                    {license.createdAt ? ` · added ${formatShortDate(license.createdAt)}` : ""}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-gray-400">
                This app will use the selected license.
              </p>
            </div>
          ) : (
            <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-2.5 text-xs text-gray-500">
              This app will use your available app license.
            </div>
          )
        )}

        {/* Step content card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">

          {/* Per-step title + explainer */}
          {currentStep && (
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800 mb-0.5">{currentStep.title}</h2>
              <p className="text-xs text-gray-400">{currentStep.explainer}</p>
            </div>
          )}

          <div className="px-6 py-5">
            {creatingWebapp ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="h-5 w-5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                <p className="text-sm text-gray-400">Setting up your application…</p>
              </div>
            ) : (
              RenderView()
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default NewApplication;
